import { useMemo } from 'react';
import { SELECTIONMODE_MULTI } from '../../../data-table/SelectionContext';
import { useDataTableSelectionState } from '../../../../services/FormDataTableActionService';

/**
 * Custom hook for managing infrastructure list selection state
 * Wraps the generic useDataTableSelectionState with infrastructure-specific configuration
 *
 * @param {string} idMapping - The field name to use as the unique identifier for rows
 * @returns {Object} Selection state and handlers
 * @returns {boolean} dataTableActionsDisabled - Whether actions should be disabled (no selection)
 * @returns {Object} tableSelection - Current selection state with {selection, inverted} properties
 * @returns {Object} selectionData - Combined selection state and rows data
 * @returns {Function} handleTableSelectionChange - Handler for selection changes
 * @returns {Object} selectionOptions - Configuration for DataTable selection mode
 *
 * @example
 * function InfrastructureList() {
 *   const { dataTableActionsDisabled, tableSelection, selectionData, selectionOptions } =
 *     useInfrastructureSelectionState('_id');
 *
 *   return (
 *     <DataTable
 *       selectionOptions={selectionOptions}
 *       // ...
 *     />
 *   );
 * }
 */
export function useInfrastructureSelectionState(idMapping) {
    const { dataTableActionsDisabled, tableSelection, currentTableSelectionAndRows, handleTableSelectionChange } =
        useDataTableSelectionState();

    const selectionOptions = useMemo(() => {
        return {
            mode: SELECTIONMODE_MULTI,
            allowSelectAll: true,
            idMapping,
            onChange: handleTableSelectionChange,
        };
    }, [idMapping, handleTableSelectionChange]);

    return {
        dataTableActionsDisabled,
        tableSelection,
        selectionData: currentTableSelectionAndRows,
        handleTableSelectionChange,
        selectionOptions,
    };
}
